import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type { AppManageSubscriptionsServicesModel } from 'walden-fast/routes/app/manage/subscriptions/services';
import type UserService from 'walden-fast/services/user';
import {
  SERVICE_NAMES,
  type ServiceName,
} from 'walden-fast/utils/constants/services';
import type { UserAccountService } from 'walden-fast/types/fast-backend/user';
import { t } from 'walden-fast/utils/intl';

const SERVICE_TRANSLATION_KEYS: Record<ServiceName, string> = {
  [SERVICE_NAMES.WALDEN_CONNECT]: t(
    'pages.manage.subscriptions.services.serviceName.waldenConnect',
  ),
  [SERVICE_NAMES.WALDEN_PULSE]: t(
    'pages.manage.subscriptions.services.serviceName.waldenPulse',
  ),
  [SERVICE_NAMES.WALDEN_VAS]: t(
    'pages.manage.subscriptions.services.serviceName.waldenVas',
  ),
  [SERVICE_NAMES.WALDEN_BOX]: t(
    'pages.manage.subscriptions.services.serviceName.waldenBox',
  ),
  [SERVICE_NAMES.WALDEN_TRACE]: t(
    'pages.manage.subscriptions.services.serviceName.waldenTrace',
  ),
  [SERVICE_NAMES.WALDEN_CHAT]: t(
    'pages.manage.subscriptions.services.serviceName.waldenChat',
  ),
  [SERVICE_NAMES.WALDEN_VIEW_WAREHOUSING]: t(
    'pages.manage.subscriptions.services.serviceName.waldenView.warehousing',
  ),
  [SERVICE_NAMES.WALDEN_VIEW_TRANSPORT]: t(
    'pages.manage.subscriptions.services.serviceName.waldenView.transport',
  ),
  [SERVICE_NAMES.WALDEN_VIEW_OTC]: t(
    'pages.manage.subscriptions.services.serviceName.waldenView.order2Cash',
  ),
  [SERVICE_NAMES.WALDEN_ORCHESTRATION]: t(
    'pages.manage.subscriptions.services.serviceName.waldenView.orchestration',
  ),
  [SERVICE_NAMES.WALDEN_DASHBOARD]: t(
    'pages.manage.subscriptions.services.serviceName.dashboard',
  ),
  [SERVICE_NAMES.WALDEN_GREEN_LEVEL0]: t(
    'pages.manage.subscriptions.services.serviceName.waldenGreen.level0',
  ),
  [SERVICE_NAMES.WALDEN_GREEN_LEVEL1]: t(
    'pages.manage.subscriptions.services.serviceName.waldenGreen.level1',
  ),
  [SERVICE_NAMES.WALDEN_GREEN_LEVEL2]: t(
    'pages.manage.subscriptions.services.serviceName.waldenGreen.level2',
  ),
  [SERVICE_NAMES.WALDEN_GREEN_LEVEL3]: t(
    'pages.manage.subscriptions.services.serviceName.waldenGreen.level3',
  ),
  [SERVICE_NAMES.WALDEN_ML]: t(
    'pages.manage.subscriptions.services.serviceName.waldenMl',
  ),
  [SERVICE_NAMES.WALDEN_VIEW_ORDER_TRACKING]: t(
    'pages.manage.subscriptions.services.serviceName.orderTracking',
  ),
};

export default class AppManageSubscriptionsServicesController extends Controller {
  declare model: AppManageSubscriptionsServicesModel;

  @service('user') declare userService: UserService;

  serviceNameTranslationKey = (service: UserAccountService) => {
    const serviceName = this.userService.getServiceNameKey(
      service.serviceUniqueName,
      service.subServiceUniqueName,
    );

    if (!serviceName) {
      console.warn(
        `No service mapping found for: ${service.serviceUniqueName}.${service.subServiceUniqueName}`,
      );
      return '';
    }

    return SERVICE_TRANSLATION_KEYS[serviceName];
  };

  userHasServiceEnabled = (service: UserAccountService) => {
    const serviceName = this.userService.getServiceNameKey(
      service.serviceUniqueName,
      service.subServiceUniqueName,
    );

    if (!serviceName) {
      console.warn(
        `No service mapping found for: ${service.serviceUniqueName}.${service.subServiceUniqueName}`,
      );
      return false;
    }

    return this.userService.canAccessService(serviceName);
  };

  @action
  toggle(service: UserAccountService, value: boolean, event: Event) {
    const serviceName = this.userService.getServiceNameKey(
      service.serviceUniqueName,
      service.subServiceUniqueName,
    );

    if (!serviceName) {
      console.warn(
        `No service mapping found for: ${service.serviceUniqueName}.${service.subServiceUniqueName}`,
      );
      return false;
    }

    this.userService.toggleService(
      serviceName,
      (event.currentTarget as HTMLInputElement).checked,
    );
  }
}
